/**
 * Old Age Security (OAS) Service
 * Implements accurate 2024 OAS benefit calculations with proper clawback logic
 * Based on Government of Canada OAS benefit rules
 */

export interface OASCalculationParams {
  age: number;
  taxableIncome: number;
  yearsDelay?: number; // 0-5 years
  residencyYears?: number; // Years of Canadian residency after age 18
  currentAge?: number; // For inflation calculations
}

export interface OASResult {
  monthlyBenefit: number;
  annualBenefit: number;
  baseAmount: number;
  clawbackAmount: number;
  delayBonus: number;
  residencyFactor: number;
  effectiveClawbackRate: number;
  isEligible: boolean;
  clawbackThreshold: number;
  fullClawbackThreshold: number;
}

export class OASService {
  // 2024 OAS Constants (updated annually by government)
  private static readonly OAS_2024_CONSTANTS = {
    MAX_MONTHLY_BENEFIT: 713.34, // Maximum monthly OAS benefit (Q4 2024)
    MAX_ANNUAL_BENEFIT: 8560.08, // Maximum annual OAS benefit
    CLAWBACK_THRESHOLD: 90997, // 2024 OAS recovery threshold
    FULL_CLAWBACK_THRESHOLD: 148065, // Income level where OAS is fully clawed back
    CLAWBACK_RATE: 0.15, // 15% clawback rate
    DELAY_BONUS_MONTHLY: 0.006, // 0.6% per month delay bonus
    MINIMUM_ELIGIBILITY_AGE: 65,
    MAXIMUM_DELAY_YEARS: 5,
    FULL_RESIDENCY_YEARS: 40, // Years needed for full OAS
    MINIMUM_RESIDENCY_YEARS: 10, // Minimum years for any OAS
    INFLATION_RATE: 0.025 // Assumed annual inflation rate for projections
  };

  /**
   * Calculate OAS benefit with comprehensive clawback logic
   */
  static calculateOAS(params: OASCalculationParams): OASResult {
    const {
      age,
      taxableIncome,
      yearsDelay = 0,
      residencyYears = 40,
      currentAge = age
    } = params;

    // Initialize result
    const result: OASResult = {
      monthlyBenefit: 0,
      annualBenefit: 0,
      baseAmount: 0,
      clawbackAmount: 0,
      delayBonus: 0,
      residencyFactor: 1,
      effectiveClawbackRate: 0,
      isEligible: false,
      clawbackThreshold: this.OAS_2024_CONSTANTS.CLAWBACK_THRESHOLD,
      fullClawbackThreshold: this.OAS_2024_CONSTANTS.FULL_CLAWBACK_THRESHOLD
    };

    // Validate parameters
    const validatedDelay = Math.min(this.OAS_2024_CONSTANTS.MAXIMUM_DELAY_YEARS, Math.max(0, yearsDelay));
    const eligibilityAge = this.OAS_2024_CONSTANTS.MINIMUM_ELIGIBILITY_AGE + validatedDelay;

    // Check basic eligibility
    if (age < eligibilityAge || residencyYears < this.OAS_2024_CONSTANTS.MINIMUM_RESIDENCY_YEARS) {
      return result;
    }

    result.isEligible = true;

    // Calculate inflation adjustment for future years
    const yearsFromNow = age - currentAge;
    const inflationFactor = Math.pow(1 + this.OAS_2024_CONSTANTS.INFLATION_RATE, yearsFromNow);

    // Calculate base amount with inflation
    let baseAmount = this.OAS_2024_CONSTANTS.MAX_ANNUAL_BENEFIT * inflationFactor;

    // Apply residency factor (prorated for less than 40 years)
    result.residencyFactor = Math.min(1, residencyYears / this.OAS_2024_CONSTANTS.FULL_RESIDENCY_YEARS);
    baseAmount *= result.residencyFactor;
    result.baseAmount = baseAmount;

    // Apply delay bonus
    const totalDelayMonths = validatedDelay * 12;
    result.delayBonus = totalDelayMonths * this.OAS_2024_CONSTANTS.DELAY_BONUS_MONTHLY;
    baseAmount *= (1 + result.delayBonus);

    // Calculate clawback thresholds with inflation
    result.clawbackThreshold = this.OAS_2024_CONSTANTS.CLAWBACK_THRESHOLD * inflationFactor;
    result.fullClawbackThreshold = this.OAS_2024_CONSTANTS.FULL_CLAWBACK_THRESHOLD * inflationFactor;

    // Apply clawback calculation
    let finalBenefit = baseAmount;
    if (taxableIncome > result.clawbackThreshold) {
      const clawbackableIncome = Math.min(
        taxableIncome - result.clawbackThreshold,
        result.fullClawbackThreshold - result.clawbackThreshold
      );
      
      result.clawbackAmount = clawbackableIncome * this.OAS_2024_CONSTANTS.CLAWBACK_RATE;
      result.effectiveClawbackRate = result.clawbackAmount / baseAmount;
      
      finalBenefit = Math.max(0, baseAmount - result.clawbackAmount);
    }

    result.annualBenefit = finalBenefit;
    result.monthlyBenefit = finalBenefit / 12;

    return result;
  }

  /**
   * Test OAS calculations with various income scenarios
   */
  static runOASTestScenarios(): Array<{
    scenario: string;
    params: OASCalculationParams;
    result: OASResult;
    expectedOutcome: string;
  }> {
    const testScenarios = [
      {
        scenario: "Low income retiree (no clawback)",
        params: { age: 65, taxableIncome: 30000, residencyYears: 40 },
        expectedOutcome: "Full OAS benefit"
      },
      {
        scenario: "Middle income retiree (partial clawback)",
        params: { age: 65, taxableIncome: 100000, residencyYears: 40 },
        expectedOutcome: "Partial OAS clawback"
      },
      {
        scenario: "High income retiree (full clawback)",
        params: { age: 65, taxableIncome: 160000, residencyYears: 40 },
        expectedOutcome: "Full OAS clawback (zero benefit)"
      },
      {
        scenario: "Delayed OAS (age 70, 5-year delay)",
        params: { age: 70, taxableIncome: 50000, yearsDelay: 5, residencyYears: 40 },
        expectedOutcome: "OAS with 36% delay bonus"
      },
      {
        scenario: "Partial residency (25 years)",
        params: { age: 65, taxableIncome: 40000, residencyYears: 25 },
        expectedOutcome: "62.5% of full OAS (25/40)"
      },
      {
        scenario: "Minimum residency (10 years)",
        params: { age: 65, taxableIncome: 20000, residencyYears: 10 },
        expectedOutcome: "25% of full OAS (10/40)"
      },
      {
        scenario: "Future retiree (inflation adjustment)",
        params: { age: 65, taxableIncome: 80000, residencyYears: 40, currentAge: 55 },
        expectedOutcome: "OAS with 10 years of inflation"
      },
      {
        scenario: "Clawback threshold edge case",
        params: { age: 65, taxableIncome: 90997, residencyYears: 40 },
        expectedOutcome: "Just at clawback threshold"
      }
    ];

    return testScenarios.map(test => ({
      ...test,
      result: this.calculateOAS(test.params)
    }));
  }

  /**
   * Calculate OAS optimization strategies
   */
  static calculateOptimalOASStrategy(
    baseIncome: number,
    currentAge: number,
    residencyYears: number = 40
  ): {
    takeAt65: OASResult;
    takeAt70: OASResult;
    recommendation: string;
    lifetimeValueComparison: {
      takeAt65: number;
      takeAt70: number;
      breakEvenAge: number;
    };
  } {
    const takeAt65 = this.calculateOAS({
      age: 65,
      taxableIncome: baseIncome,
      yearsDelay: 0,
      residencyYears,
      currentAge
    });

    const takeAt70 = this.calculateOAS({
      age: 70,
      taxableIncome: baseIncome,
      yearsDelay: 5,
      residencyYears,
      currentAge
    });

    // Calculate lifetime value (assuming life expectancy of 85)
    const lifeExpectancy = 85;
    const yearsReceiving65 = lifeExpectancy - 65;
    const yearsReceiving70 = lifeExpectancy - 70;

    const lifetimeValue65 = takeAt65.annualBenefit * yearsReceiving65;
    const lifetimeValue70 = takeAt70.annualBenefit * yearsReceiving70;

    // Calculate break-even age
    const annualDifference = takeAt70.annualBenefit - takeAt65.annualBenefit;
    const lostBenefits = takeAt65.annualBenefit * 5; // 5 years of lost benefits
    const breakEvenAge = annualDifference > 0 ? 70 + (lostBenefits / annualDifference) : 85;

    let recommendation: string;
    if (takeAt65.clawbackAmount === 0 && takeAt70.clawbackAmount === 0) {
      recommendation = lifetimeValue70 > lifetimeValue65 ? 
        "Delay to 70 for higher lifetime value" : 
        "Take at 65 for earlier cash flow";
    } else if (takeAt65.clawbackAmount > 0) {
      recommendation = "Consider delaying to reduce clawback impact";
    } else {
      recommendation = "Take at 65 to maximize total benefits";
    }

    return {
      takeAt65,
      takeAt70,
      recommendation,
      lifetimeValueComparison: {
        takeAt65: lifetimeValue65,
        takeAt70: lifetimeValue70,
        breakEvenAge
      }
    };
  }

  /**
   * Format OAS result for display
   */
  static formatOASResult(result: OASResult): string {
    if (!result.isEligible) {
      return "Not eligible for OAS";
    }

    const lines = [
      `Monthly OAS: $${result.monthlyBenefit.toFixed(2)}`,
      `Annual OAS: $${result.annualBenefit.toFixed(2)}`,
    ];

    if (result.clawbackAmount > 0) {
      lines.push(`Clawback: $${result.clawbackAmount.toFixed(2)} (${(result.effectiveClawbackRate * 100).toFixed(1)}%)`);
    }

    if (result.delayBonus > 0) {
      lines.push(`Delay bonus: ${(result.delayBonus * 100).toFixed(1)}%`);
    }

    if (result.residencyFactor < 1) {
      lines.push(`Residency factor: ${(result.residencyFactor * 100).toFixed(1)}%`);
    }

    return lines.join('\n');
  }
}

export default OASService; 